import { useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const BASE_URL = "https://www.ohyafinance.com";

// 貸款類型對應的中文名稱和描述
const loanTypeInfo: Record<
  string,
  { name: string; description: string; subtitle?: string }
> = {
  student: {
    name: "學生貸款",
    subtitle: "學生學利貸",
    description:
      "歐悅金融科技提供靈活的學生貸款服務，正規合法輕鬆借！申辦2-10萬的學生貸，享受即時線上申請與審核，且還款期限最長可達36期！專為18-28歲學生設計，審核門檻低，有可證明之收入即可申請。",
  },
  phone: {
    name: "手機貸款",
    subtitle: "手機隨機貸",
    description:
      "歐悅金融科技提供靈活的手機貸款服務，只要您自有手機，不留機也能輕鬆借！申辦3-12萬的靈活手機貸，享受即時線上申請與審核，且還款期限最長可達36期！",
  },
  "two-wheel": {
    name: "機車原融",
    subtitle: "騎夢駕享貸",
    description:
      "歐悅金融科技提供靈活的機車原融服務，無貸款機車便能輕鬆借！申辦10-45萬的機車原融，享受即時線上申請與審核，且還款期限最長可達36期！",
  },
  mortgage: {
    name: "不動產二胎",
    subtitle: "樂享新居貸",
    description:
      "歐悅金融科技提供靈活的不動產二胎服務，無需其他抵押就能輕鬆借！申辦最高市價110%的不動產二胎，享受即時線上申請與審核，且還款期限最長可達10年(120期)！",
  },
  policy: {
    name: "保單質押",
    description:
      "歐悅金融科技提供靈活的保單質押服務，無需其他抵押也能輕鬆借！申辦最高保單價值100%的保單質押，享受即時線上申請與審核，且還款期限最長可達36期！",
  },
  "labor-insure": {
    name: "勞保貸款",
    subtitle: "勞保悠活貸",
    description:
      "歐悅金融科技提供靈活的勞保貸款服務，無需抵押也能輕鬆借！申辦最高20倍投保金額的勞保貸，享受即時線上申請與審核，且還款期限最長可達36期！",
  },
  "labor-relief": {
    name: "無勞保貸款",
    subtitle: "勞工盡力貸",
    description:
      "歐悅金融科技提供靈活的無勞保貸款服務，無薪轉也能輕鬆借款！申辦3-10萬的無勞保貸，享受即時線上申請與審核，且還款期限最長可達36期！",
  },
};

// 從 pathname 解析路由參數
const parsePathname = (pathname: string) => {
  const parts = pathname.split("/").filter(Boolean);
  return {
    type: parts[0] || null,
    subtype: parts[1] || null,
    category: parts[2] || null,
  };
};

// 各頁面的 meta 資訊配置
const getPageMeta = (pathname: string, params: any) => {
  // 優先使用 pathname 解析，因為 params 可能不會及時更新
  const routeParams = parsePathname(pathname);
  const { type, subtype } = routeParams;

  // 首頁
  if (pathname === "/" || (!type && !subtype)) {
    return {
      title: "歐悅金融科技｜智慧融資・資產管理・數位金融｜誠信・創新・價值領航",
      description:
        "歐悅金融科技致力於數位金融價值領航。提供專業智慧融資解決方案、資產管理與多元金融服務，透過嚴謹資信控管與科技創新，創造穩健成長價值。",
      canonical: `${BASE_URL}/`,
      ogImage: `${BASE_URL}/logo480.png`,
    };
  }

  // 貸款產品主頁
  if (type === "loan" && !subtype) {
    return {
      title: "貸款產品 - 歐悅金融科技",
      description:
        "歐悅金融科技提供多元貸款產品，包含學生貸款、手機貸款、機車原融、不動產二胎、保單質押、勞保貸款、無勞保貸款等，線上申請快速審核，正規合法輕鬆借！",
      canonical: `${BASE_URL}/loan`,
      ogImage: `${BASE_URL}/logo480.png`,
    };
  }

  // 貸款產品詳情頁
  if (type === "loan" && subtype) {
    const loanInfo = loanTypeInfo[subtype] || {
      name: "貸款",
      description: "歐悅金融科技提供專業，快速審核的貸款服務！",
    };
    const displayName = loanInfo.subtitle
      ? `${loanInfo.name} - ${loanInfo.subtitle}`
      : loanInfo.name;
    return {
      title: `${displayName} - 歐悅金融科技`,
      description: loanInfo.description,
      canonical: `${BASE_URL}/loan/${subtype}`,
      ogImage: `${BASE_URL}/logo480.png`,
    };
  }

  // 最新消息頁面
  if (type === "news" && !subtype) {
    return {
      title: "最新消息 - 歐悅金融科技",
      description: "歐悅金融科技最新消息、優惠活動、產品資訊與重要公告。",
      canonical: `${BASE_URL}/news`,
      ogImage: `${BASE_URL}/logo480.png`,
    };
  }

  // 消息詳情頁面
  if (type === "news" && subtype === "detail") {
    return {
      title: "消息內容 - 歐悅金融科技",
      description:
        "歐悅金融科技最新消息詳細內容，包含優惠活動、產品資訊與重要公告。",
      canonical: `${BASE_URL}${pathname}`,
      ogImage: `${BASE_URL}/logo480.png`,
    };
  }

  // 申貸進度查詢頁面
  if (type === "my-loan") {
    return {
      title: "申貸進度查詢 - 歐悅金融科技｜隨時掌握申請狀態",
      description:
        "歐悅金融科技申貸進度查詢系統，讓您隨時掌握貸款申請狀態、審核進度與相關資訊，透明化的服務流程。",
      canonical: `${BASE_URL}/my-loan`,
      ogImage: `${BASE_URL}/logo480.png`,
    };
  }

  // 預設值（其他頁面）
  return {
    title: "歐悅金融科技｜智慧融資・資產管理・數位金融",
    description:
      "歐悅金融科技提供靈活的貸款服務，正規合法輕鬆借！提供學生貸、機車貸款、手機貸款、勞保貸款、無勞保貸款、保單質押、不動產二胎等多項分期付款服務。",
    canonical: `${BASE_URL}${pathname}`,
    ogImage: `${BASE_URL}/logo480.png`,
  };
};

const PageMeta = () => {
  const location = useLocation();
  const params = useParams();

  const meta = useMemo(() => getPageMeta(location.pathname, params), [
    location.pathname,
    params,
  ]);

  return (
    <Helmet>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={meta.canonical} />
      {meta.ogImage && <meta property="og:image" content={meta.ogImage} />}
      <link rel="canonical" href={meta.canonical} />
    </Helmet>
  );
};

export default PageMeta;
